import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Button,
  Chip,
  Divider,
  CircularProgress,
  Paper,
} from '@mui/material';
import { LocationOn, Refresh } from '@mui/icons-material';
import { useGame } from '../GameState';

// Default image if cardinal photo is missing
const defaultImage = 'https://via.placeholder.com/300x200?text=No+Image+Available';

// Number of cardinals to display for selection
const CARDINALS_TO_DISPLAY = 9;

function CharacterCreation() {
  const { cardinals, selectPlayerCardinal, loading, error } = useGame();
  const [selectedCardinal, setSelectedCardinal] = useState(null);
  const [randomCardinals, setRandomCardinals] = useState([]);

  // Select random cardinals when component mounts or when refresh is clicked
  useEffect(() => {
    if (cardinals.length > 0) {
      selectRandomCardinals();
    }
  }, [cardinals]);

  // Function to randomly select cardinals
  const selectRandomCardinals = () => {
    // Make a copy of the cardinals array to avoid modifying the original
    const shuffled = [...cardinals];
    
    // Fisher-Yates shuffle algorithm
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    
    // Select the first CARDINALS_TO_DISPLAY cardinals
    setRandomCardinals(shuffled.slice(0, CARDINALS_TO_DISPLAY));
    
    // Clear any previously selected cardinal
    setSelectedCardinal(null);
  };

  // Handle cardinal selection
  const handleSelectCardinal = (cardinal) => {
    setSelectedCardinal(cardinal);
    
    // Dispatch custom event with selected cardinal
    const customEvent = new CustomEvent('cardinalSelected', { 
      detail: { cardinal },
      bubbles: true 
    });
    window.dispatchEvent(customEvent);
  };

  // Confirm selection and proceed to next phase
  const handleConfirmSelection = () => {
    if (selectedCardinal) {
      selectPlayerCardinal(selectedCardinal);
    }
  };

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading cardinals...</Typography>
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Paper 
        elevation={3}
        sx={{ 
          p: 3, 
          textAlign: 'center',
          bgcolor: 'error.light',
          color: 'error.contrastText'
        }}
      >
        <Typography variant="h5" gutterBottom>
          Error Loading Cardinals
        </Typography>
        <Typography>{error}</Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom>
          Select Your Cardinal
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={<Refresh />} 
          onClick={selectRandomCardinals}
          size="small"
        >
          Refresh Candidates
        </Button>
      </Box>
      
      <Typography variant="body1" paragraph align="center">
        Choose the cardinal you will play as during the conclave. Each cardinal has unique
        attributes that will affect your strategy and chances of success.
      </Typography>
      
      <Divider sx={{ mb: 4 }} />
      
      {/* Cardinal selection grid */}
      <Grid container spacing={3}>
        {randomCardinals.map((cardinal, index) => (
          <Grid item key={index} xs={12} sm={6} md={4}>
            <Box 
              sx={{ 
                height: '100%', 
                position: 'relative',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-5px)'
                },
                transition: 'transform 0.3s ease',
              }}
              onClick={() => handleSelectCardinal(cardinal)}
            >
              {/* Decorative frame */}
              <Box 
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  border: '1px solid rgba(139, 69, 19, 0.4)',
                  borderRadius: 2,
                  zIndex: 0,
                  transform: 'translate(8px, 8px)',
                  pointerEvents: 'none'
                }}
              />
              
              {/* Main Card */}
              <Paper
                elevation={3}
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  position: 'relative',
                  zIndex: 1,
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: '#f9f2e3', // Parchment color
                  backgroundImage: 'repeating-linear-gradient(to right, rgba(139, 69, 19, 0.02), rgba(139, 69, 19, 0.03) 1px, #f9f2e3 1px, #f9f2e3 20px)',
                  backgroundBlendMode: 'multiply',
                  border: selectedCardinal?.id === cardinal.id 
                    ? '2px solid #b71c1c' 
                    : '1px solid rgba(139, 69, 19, 0.6)',
                  boxShadow: selectedCardinal?.id === cardinal.id 
                    ? '0 0 15px rgba(183, 28, 28, 0.5)' 
                    : '0 4px 8px rgba(0, 0, 0, 0.1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'url("data:image/png;base64,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") repeat',
                    opacity: 0.03,
                    zIndex: 0
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '5px',
                    backgroundImage: 'linear-gradient(90deg, transparent, rgba(139, 69, 19, 0.2), transparent)',
                    zIndex: 1
                  }
                }}
              >
                {/* Corner Ornaments */}
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    top: 5, 
                    left: 5, 
                    width: '15px', 
                    height: '15px', 
                    borderTop: '1px solid rgba(139, 69, 19, 0.5)',
                    borderLeft: '1px solid rgba(139, 69, 19, 0.5)',
                    zIndex: 1
                  }} 
                />
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    top: 5, 
                    right: 5, 
                    width: '15px', 
                    height: '15px', 
                    borderTop: '1px solid rgba(139, 69, 19, 0.5)',
                    borderRight: '1px solid rgba(139, 69, 19, 0.5)',
                    zIndex: 1
                  }} 
                />
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    bottom: 5, 
                    left: 5, 
                    width: '15px', 
                    height: '15px', 
                    borderBottom: '1px solid rgba(139, 69, 19, 0.5)',
                    borderLeft: '1px solid rgba(139, 69, 19, 0.5)',
                    zIndex: 1
                  }} 
                />
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    bottom: 5, 
                    right: 5, 
                    width: '15px', 
                    height: '15px', 
                    borderBottom: '1px solid rgba(139, 69, 19, 0.5)',
                    borderRight: '1px solid rgba(139, 69, 19, 0.5)',
                    zIndex: 1
                  }} 
                />
                
                {/* Cardinal Photo with Decorative Frame */}
                <Box sx={{ position: 'relative', p: 2 }}>
                  <Box 
                    sx={{ 
                      position: 'relative',
                      height: '180px',
                      borderRadius: 1,
                      border: '1px solid rgba(139, 69, 19, 0.6)',
                      boxShadow: 'inset 0 0 8px rgba(139, 69, 19, 0.3)',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 5,
                        left: 5,
                        right: 5,
                        bottom: 5,
                        border: '1px solid rgba(139, 69, 19, 0.3)',
                        zIndex: 1,
                        pointerEvents: 'none'
                      }
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="180"
                      image={cardinal.photo_url || defaultImage}
                      alt={cardinal.name}
                      sx={{ 
                        objectFit: 'contain', 
                        bgcolor: '#f5f5f5',
                        objectPosition: 'center',
                        zIndex: 0
                      }}
                    />
                  </Box>
                </Box>
                
                {/* Cardinal Info */}
                <CardContent sx={{ flexGrow: 1, position: 'relative', zIndex: 1 }}>
                  {/* Decorative Title Separator */}
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 2,
                      '&::before, &::after': {
                        content: '""',
                        flex: 1,
                        borderBottom: '1px solid rgba(139, 69, 19, 0.4)',
                        mx: 1
                      }
                    }}
                  >
                    <Typography 
                      variant="h6" 
                      component="h2" 
                      align="center"
                      sx={{ 
                        color: '#5D1D0D', // Deep red color for titles
                        letterSpacing: '0.05em',
                        lineHeight: 1.2
                      }}
                    >
                      {cardinal.name}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LocationOn sx={{ color: '#5D1D0D', mr: 1, fontSize: '0.9rem' }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#5D1D0D',
                        fontStyle: 'italic'
                      }}
                    >
                      {cardinal.country}
                    </Typography>
                  </Box>
                  
                  {cardinal.traits && (
                    <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {cardinal.traits.map((trait, i) => (
                        <Chip 
                          key={i}
                          label={trait}
                          size="small"
                          sx={{ 
                            fontSize: '0.75rem',
                            backgroundColor: 'rgba(139, 69, 19, 0.1)', 
                            border: '1px solid rgba(139, 69, 19, 0.3)',
                            color: '#5D1D0D'
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  
                  {/* Decorative Divider */}
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      my: 1.5,
                      '&::before, &::after': {
                        content: '""',
                        flex: 1,
                        height: '1px',
                        background: 'linear-gradient(to right, transparent, rgba(139, 69, 19, 0.4), transparent)',
                      }
                    }}
                  >
                    <Box 
                      component="span" 
                      sx={{ 
                        mx: 2, 
                        color: 'rgba(139, 69, 19, 0.6)', 
                        fontSize: '1rem',
                        lineHeight: 0
                      }}
                    >
                      ✠
                    </Box>
                  </Box>
                  
                  <Typography 
                    variant="body2"
                    sx={{
                      color: '#5D1D0D',
                      textAlign: 'center',
                      fontFamily: '"Gentium Book Basic", serif'
                    }}
                  >
                    Piety: {cardinal.piety} • Influence: {cardinal.influence} • 
                    Diplomacy: {cardinal.diplomacy}
                  </Typography>
                </CardContent>
              </Paper>
            </Box>
          </Grid>
        ))}
      </Grid>
      
      {/* Confirmation section */}
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Button 
          variant="contained" 
          size="large" 
          onClick={handleConfirmSelection}
          disabled={!selectedCardinal}
          sx={{ 
            mt: 2, 
            px: 4,
            backgroundColor: '#5D1D0D',
            '&:hover': {
              backgroundColor: '#7A2A17',
            },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(93, 29, 13, 0.3)',
            }
          }}
        >
          {selectedCardinal 
            ? `Confirm Selection: ${selectedCardinal.name}` 
            : 'Select a Cardinal First'}
        </Button>
      </Box>
    </Box>
  );
}

export default CharacterCreation; 